// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import type { State } from "@store/reducers";
import actionCreators from "@modules/service/actionCreator";
import * as userPermissionsSelectors from "@modules/service/selectors/user-permissions";
import { SectionComponent, ColumnsContainer } from "@components";
import { getUserId } from "@modules/account/selectors";
import { UserPermissionsEditModal } from "@modules/service/components";
import type { EffectiveUserPermissions } from "@modules/service/reducer/user-permissions/types";

type PropsFromState = $Exact<{
  isLoading: boolean,
  currentUserId: number | null,
  effectiveUserPermissions: EffectiveUserPermissions,
  getManagementUsersSchema: Object,
  getEmployeeUsersSchema: Object,
  getAbsenceUpdatePermissionsSchema: Object,
  showEmployeeUsersModal: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getEffectiveUserPermissions: typeof actionCreators.GetEffectiveUserPermissions,
  getEffectiveUserPermissionsSchema: typeof actionCreators.GetEffectiveUserPermissionsSchema,
  updateEffectiveUserPermissions: typeof actionCreators.UpdateEffectiveUserPermissions,
  toggleEmployeeUsersModal: typeof actionCreators.ToggleEmployeeUsersModal,
}>;

type Props = $Exact<{
  sectionName: string,
  clientId: number,
  ...PropsFromDispatch,
  ...PropsFromState,
  ...PropsFromLocalize,
}>;

type ModalData = $Exact<{
  title: string,
  schema: Object,
  name: string,
}>;

type ComponentState = $Exact<{
  modalData: ?ModalData,
}>;

export class UserPermissions extends React.Component<Props, ComponentState> {
  state = {
    modalData: null,
  };

  componentDidMount() {
    this.props.getEffectiveUserPermissions({ clientId: this.props.clientId });
    this.props.getEffectiveUserPermissionsSchema();
  }

  toggleModal = (modalData: ModalData | null = null) => {
    this.props.toggleEmployeeUsersModal(!this.props.showEmployeeUsersModal);
    this.setState({
      modalData,
    });
  };

  updateUserPermissions = (userPermissions: EffectiveUserPermissions) => {
    this.props.updateEffectiveUserPermissions({
      clientId: this.props.clientId,
      userPermissions,
    });
  };

  get effectiveUserPermissions() {
    return this.props.effectiveUserPermissions;
  }

  get rowsData() {
    const {
      notificationAdmin,
      reportAbsences,
      updateAbsences,
      permittedExtension,
      closeAbsences,
    } = this.effectiveUserPermissions;

    const result = [
      {
        columns: [
          {
            columnTitle: this.props.t("MANAGEMENT_USERS"),
            columnButtons: [
              {
                materialIcon: "edit",
                text: this.props.t("EDIT"),
                onClick: this.toggleModal.bind(null, {
                  title: this.props.t("MANAGEMENT_USERS"),
                  schema: this.props.getManagementUsersSchema,
                  name: "managementUsers",
                }),
              },
            ],
            data: [
              [
                this.props.t("NOTIFICATION_ADMIN"),
                !!notificationAdmin ? this.props.t(notificationAdmin) : "",
              ],
              [
                this.props.t("EXCLUDING_ABSENCES_FROM_ALERTS"),
                <div
                  key="2"
                  className={
                    this.effectiveUserPermissions.excludeAbsencesFromAlerts
                      ? "active-badge"
                      : "inactive-badge"
                  }
                >
                  {this.props.t(
                    this.effectiveUserPermissions.excludeAbsencesFromAlerts ? "ACTIVE" : "INACTIVE",
                  )}
                </div>,
              ],
              [
                this.props.t("ALLOW_MANAGER_TO_EDIT_EMPLOYEES_SHIFT_PATTERN"),
                <div
                  key="2"
                  className={
                    this.effectiveUserPermissions.allowManagerEditEmployeesShiftPattern
                      ? "active-badge"
                      : "inactive-badge"
                  }
                >
                  {this.props.t(
                    this.effectiveUserPermissions.allowManagerEditEmployeesShiftPattern
                      ? "ACTIVE"
                      : "INACTIVE",
                  )}
                </div>,
              ],
            ],
          },
          {
            columnTitle: this.props.t("EMPLOYEE_USERS"),
            columnButtons: [
              {
                materialIcon: "edit",
                text: this.props.t("EDIT"),
                onClick: this.toggleModal.bind(null, {
                  title: this.props.t("EMPLOYEE_USERS"),
                  schema: this.props.getEmployeeUsersSchema,
                  name: "employeeUsers",
                }),
              },
            ],
            data: [
              [
                this.props.t("EMPLOYEE_ACCOUNTS_PERMITTED"),
                <div
                  key="2"
                  className={
                    this.effectiveUserPermissions.employeeAccounts
                      ? "active-badge"
                      : "inactive-badge"
                  }
                >
                  {this.props.t(
                    this.effectiveUserPermissions.employeeAccounts ? "ACTIVE" : "INACTIVE",
                  )}
                </div>,
              ],
              ...(this.effectiveUserPermissions.employeeAccounts
                ? [
                    [
                      this.props.t("BENCHMARKING_STATISTICS"),
                      <div
                        key="2"
                        className={
                          this.effectiveUserPermissions.benchmarkingStatistics
                            ? "active-badge"
                            : "inactive-badge"
                        }
                      >
                        {this.props.t(
                          this.effectiveUserPermissions.benchmarkingStatistics
                            ? "ACTIVE"
                            : "INACTIVE",
                        )}
                      </div>,
                    ],
                    [
                      this.props.t("ABSENCE_MONITORING_DETAILS"),
                      <div
                        key="2"
                        className={
                          this.effectiveUserPermissions.absenceMonitoringDetails
                            ? "active-badge"
                            : "inactive-badge"
                        }
                      >
                        {this.props.t(
                          this.effectiveUserPermissions.absenceMonitoringDetails
                            ? "ACTIVE"
                            : "INACTIVE",
                        )}
                      </div>,
                    ],
                    [
                      this.props.t("VIEW_COMPLETED_RTW_INTERVIEWS"),
                      <div
                        key="2"
                        className={
                          this.effectiveUserPermissions.viewRtwForms
                            ? "active-badge"
                            : "inactive-badge"
                        }
                      >
                        {this.props.t(
                          this.effectiveUserPermissions.viewRtwForms ? "ACTIVE" : "INACTIVE",
                        )}
                      </div>,
                    ],
                  ]
                : []),
            ],
          },
          {
            columnTitle: this.props.t("ABSENCE_UPDATE_PERMISSIONS"),
            columnButtons: [
              {
                materialIcon: "edit",
                text: this.props.t("EDIT"),
                onClick: this.toggleModal.bind(null, {
                  title: this.props.t("ABSENCE_UPDATE_PERMISSIONS"),
                  schema: this.props.getAbsenceUpdatePermissionsSchema,
                  name: "absenceUpdatePermissions",
                }),
              },
            ],
            data: [
              [
                this.props.t("REPORT_ABSENCES"),
                !!reportAbsences ? this.props.t(reportAbsences) : "",
              ],
              [
                this.props.t("UPDATE_ABSENCES"),
                !!updateAbsences ? this.props.t(updateAbsences) : "",
              ],

              [
                this.props.t("PERMITTED_EXTENSION", {
                  [this.props.t("DAYS")]: this.props.t("DAYS"),
                }),
                `${permittedExtension}`,
              ],
              [this.props.t("CLOSE_ABSENCES"), !!closeAbsences ? this.props.t(closeAbsences) : ""],
            ],
          },
        ],
      },
    ];
    const permissions = this.props.effectiveUserPermissions || {};

    if (permissions.employeeAccounts === false) {
      result[0].columns.splice(2, 1);
    }

    if (permissions.employeeAccounts && permissions.updateAbsences === "NONE") {
      result[0].columns[2].data.splice(2, 1);
    }

    return result;
  }

  render() {
    return (
      <React.Fragment>
        {this.props.effectiveUserPermissions && (
          <SectionComponent title={<h5>{this.props.t("USER_PERMISSIONS")}</h5>}>
            <ColumnsContainer className="w-100" rows={this.rowsData} />
          </SectionComponent>
        )}
        {this.props.showEmployeeUsersModal && this.state.modalData && (
          <UserPermissionsEditModal
            data={this.props.effectiveUserPermissions || {}}
            toggleModal={this.toggleModal}
            updateUserPermissions={this.updateUserPermissions}
            {...this.state.modalData}
          />
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    isLoading: userPermissionsSelectors.isLoading(state),
    currentUserId: getUserId(state),
    effectiveUserPermissions: userPermissionsSelectors.getEffectiveUserPermissions(state),
    getManagementUsersSchema: userPermissionsSelectors.getManagementUsersSchema(state),
    getEmployeeUsersSchema: userPermissionsSelectors.getEmployeeUsersSchema(state),
    getAbsenceUpdatePermissionsSchema: userPermissionsSelectors.getAbsenceUpdatePermissionsSchema(
      state,
    ),
    showEmployeeUsersModal: userPermissionsSelectors.showEmployeeUsersModal(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getEffectiveUserPermissions: actionCreators.GetEffectiveUserPermissions,
  getEffectiveUserPermissionsSchema: actionCreators.GetEffectiveUserPermissionsSchema,
  updateEffectiveUserPermissions: actionCreators.UpdateEffectiveUserPermissions,
  toggleEmployeeUsersModal: actionCreators.ToggleEmployeeUsersModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(UserPermissions));
